import React, { useMemo, useState } from "react";
// Meter Alerts Component (Migrated to V2 standard matching MeterEvents)

// Mui
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

// Custom
import CustomDataTable from "../table/CustomTable";
import useMeterEventsV2 from "../../hooks/useMeterEventsV2";
import { formatDate } from "../../utils/helper";
import moment from "moment";
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import Calendar2 from "../../assets/svg/calendar2";
import Download from "../../assets/svg/download";
import { fetchGETRequest } from "../../utils/services";
import { MeterEventsV2Response } from "../../types";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

interface MeterAlertsProps {
  meterSerialNumber: string;
}

const MeterAlerts: React.FC<MeterAlertsProps> = ({ meterSerialNumber }) => {
  const [startDate, setStartDate] = useState<Date | null>(
    moment().subtract(7, "days").toDate(),
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateEl, setDateEl] = useState<HTMLElement | null>(null);
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);
  const [isExporting, setIsExporting] = useState(false);

  // These are the dates used for the API call
  const [queryDates, setQueryDates] = useState({
    start: moment().subtract(7, "days").toDate(),
    end: new Date(),
  });

  const { data: eventsResponse, isLoading } = useMeterEventsV2({
    meterSerialNumber,
    from: moment(queryDates.start)
      .startOf("day")
      .add(timeRange[0], "minutes")
      .format("YYYY-MM-DDTHH:mm"),
    to: moment(queryDates.end)
      .startOf("day")
      .add(timeRange[1], "minutes")
      .format("YYYY-MM-DDTHH:mm"),
    skip: page * rowsPerPage,
    take: rowsPerPage,
  });

  const eventsData = eventsResponse?.data?.records || [];
  const totalCount = eventsResponse?.data?.total || 0;

  const COLUMNS = ["Date/Time", "Event Name", "Description", "Type", "Code"];

  const rowsContent = useMemo(() => {
    return eventsData.map((item, index) => (
      <TableRow key={item.id || index}>
        <TableCell>
          <Typography fontSize="13px">
            {moment(item.eventTimestamp).format("DD/MM/YYYY HH:mm:ss")}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography fontSize="13px" fontWeight={600}>
            {item.eventName || "N/A"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography fontSize="13px">{item.description || "N/A"}</Typography>
        </TableCell>
        <TableCell>
          <Typography fontSize="13px">{item.eventType || "N/A"}</Typography>
        </TableCell>
        <TableCell>
          <Typography fontSize="13px">{item.eventCode || "N/A"}</Typography>
        </TableCell>
      </TableRow>
    ));
  }, [eventsData]);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleDateSubmit = () => {
    if (startDate && endDate) {
      setQueryDates({ start: startDate, end: endDate });
      setPage(0);
    }
  };

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      const from = moment(queryDates.start)
        .startOf("day")
        .add(timeRange[0], "minutes")
        .format("YYYY-MM-DDTHH:mm");
      const to = moment(queryDates.end)
        .startOf("day")
        .add(timeRange[1], "minutes")
        .format("YYYY-MM-DDTHH:mm");

      const response = await fetchGETRequest<MeterEventsV2Response>(
        `v2/meter-events/browse?skip=0&take=100000&search=${meterSerialNumber}&from=${from}&to=${to}`,
      );

      const records = response?.data?.records || [];
      if (records.length === 0) {
        alert("No data found to export");
        return;
      }

      const headers = ["Date/Time", "Event Name", "Description", "Type", "Code"];
      const csvData = records.map((item) => [
        moment(item.eventTimestamp).format("DD/MM/YYYY HH:mm:ss"),
        item.eventName || "N/A",
        item.description || "N/A",
        item.eventType || "N/A",
        item.eventCode || "N/A",
      ]);

      const csvContent = [
        headers.join(","),
        ...csvData.map((row) =>
          row
            .map((val) => {
              const str = String(val).replace(/"/g, '""');
              return `"${str}"`;
            })
            .join(","),
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Meter_Alerts_${meterSerialNumber}_${moment().format("YYYYMMDD_HHmmss")}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Card
      sx={{
        borderWidth: "1px",
        borderColor: "#E0E0E0",
        borderRadius: 2,
        m: 2,
        boxShadow: "none",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pr: 2 }}
      >
        <CardHeader title="Meter Alerts & Events" />
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography fontSize="13px" fontWeight={500} color="textSecondary">
            {formatDate(queryDates.start, "D MMM YYYY")} -{" "}
            {formatDate(queryDates.end, "D MMM YYYY")}
          </Typography>
          <IconButton onClick={(e) => setDateEl(e.currentTarget)}>
            <Calendar2 />
          </IconButton>
          <Tooltip title="Export to CSV">
            <IconButton onClick={handleExportCSV} disabled={isExporting}>
              {isExporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Download />
              )}
            </IconButton>
          </Tooltip>
          <DateAndTimePopover
            anchorEl={dateEl}
            setAnchorEl={setDateEl}
            startDate={startDate as Date}
            endDate={endDate as Date}
            handleDateChange={handleDateChange}
            timeRange={timeRange}
            setTimeRange={(val) => setTimeRange(val as number[])}
            handleDateSubmit={handleDateSubmit}
          />
        </Stack>
      </Stack>
      <CardContent sx={{ p: 0, borderTop: "1px solid #E0E0E0" }}>
        <CustomDataTable
          columns={COLUMNS}
          rowsContent={rowsContent}
          isLoading={isLoading}
          totalCount={totalCount}
          pagination={true}
          Actions={true}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sxHeader={{ height: "40px" }}
        />
      </CardContent>
    </Card>
  );
};

export default MeterAlerts;
